import { z } from 'zod';

/**
 * Assistants and the links between conversations (docs/adr-assistants.md, which supersedes the
 * workspaces of docs/plan-desktop-mobile-redesign.md §2.3–2.7). An assistant (formerly a
 * project) has a channel its agent leads: it answers, and hands work out as tasks, run by the
 * user's automations it may use or done by an agent alone (docs/adr-tasks-and-automations.md).
 * The Crowdex workspace schemas below serve the
 * `/api/workspaces` aliases kept for older desktops; "workspace" there is not the `workspace`
 * table of the database, which is one checkout of a repository on one machine (`repositoryIds`
 * point at its logical ids).
 */

const recordId = z.string().min(1).max(128);

/** A colour of the charter's project palette, as `#rrggbb`. */
export const assistantColorSchema = z.string().regex(/^#[0-9a-f]{6}$/i);

/**
 * Colours offered when creating a project (D69), in order. None is amber: amber says "waits on
 * you" only. Colours stored before stay valid (any `#rrggbb`).
 */
export const ASSISTANT_COLORS = [
  '#8b7cf6',
  '#d47fd6',
  '#4cc27a',
  '#7fb4ff',
  '#f07178',
  '#e8835a',
  '#4fc1c9',
] as const;

/**
 * A workflow the workspace offers its projects: a definition a task provider runs
 * (docs/adr-tasks-and-roles.md §4).
 */
export const workspaceWorkflowSchema = z.object({
  /** Stable id within the workspace (`quick`, `standard`…, or a slug of a user's definition). */
  id: recordId,
  name: z.string().trim().min(1).max(80),
  /** The plugin that runs it (`tasks.v1`). */
  provider: z.string().min(1).max(128),
  /** The provider's definition it runs (a template id, a file of the workspace). */
  definition: z.string().min(1).max(512),
  /** Its steps in one line, as Settings › Workflows shows them (D63). */
  summary: z.string().max(300).default(''),
  /** What a project's agent starts for work its instructions name no workflow for; one has it. */
  isDefault: z.boolean().default(false),
});

export const workspaceKindSchema = z.enum(['personal', 'shared']);

export const crowdexWorkspaceSchema = z.object({
  id: recordId,
  /** `personal`: the user's private workspace, without members, one per user (D74). */
  kind: workspaceKindSchema,
  name: z.string().trim().min(1).max(80),
  /** The organization whose members share it; null for Personal. */
  organizationId: z.string().nullable(),
  /** Instructions every agent of the workspace reads. */
  instructions: z.string().max(20_000).default(''),
  /** Logical workspace ids (`logical_workspace.id`) of its repositories. */
  repositoryIds: z.array(recordId).max(200).default([]),
  workflows: z.array(workspaceWorkflowSchema).max(50).default([]),
  /** How many of its projects use each workflow (a task of it ran there, a schedule names it). */
  workflowProjects: z.record(z.string(), z.number().int().nonnegative()).optional(),
  /** Whether the user may change it (its creator, an admin of its organization); absent: yes. */
  canEdit: z.boolean().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/**
 * @deprecated A schedule of an assistant, before schedules were automations
 * (docs/adr-tasks-and-automations.md): migrated to an automation that starts its task.
 */
export const assistantWakeSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('schedule'),
    cronExpression: z.string().min(1).max(120),
    timezone: z.string().min(1).max(64),
    /** The workflow the schedule starts (section 8 default 5: "Daily status"). */
    workflowId: recordId.optional(),
  }),
]);

export const crowdexAssistantSchema = z.object({
  id: recordId,
  /**
   * @deprecated The workspace a former project sat in; null for an assistant made since
   * workspaces went (docs/adr-assistants.md). Only the `/api/workspaces` aliases fill it in.
   */
  workspaceId: recordId.nullable().optional(),
  name: z.string().trim().min(1).max(80),
  goal: z.string().max(2_000).default(''),
  color: assistantColorSchema,
  /**
   * Logical workspace ids of its repositories, among the workspace's. Optional: a project about
   * content, research or design has none.
   */
  repositoryIds: z.array(recordId).max(50).default([]),
  /** The saved agents (`agent_profile`) its conversations hand work to. */
  memberAgentIds: z.array(recordId).max(16).default([]),
  /**
   * @deprecated Its schedules before they were automations: empty once migrated
   * (docs/adr-tasks-and-automations.md), still sent for older desktops.
   */
  wakes: z.array(assistantWakeSchema).max(20).default([]),
  /** What every conversation of the assistant is told, its agent's choices of automation among them. */
  instructions: z.string().max(20_000).default(''),
  /**
   * The avatar it speaks as (a persona of the call plugin), at most one assistant per avatar;
   * null: the default voice (docs/adr-assistants.md). Absent from older servers.
   */
  avatarId: recordId.nullable().optional(),
  /** The user's home assistant, one per user. Absent from older servers: no. */
  isHome: z.boolean().optional(),
  /**
   * @deprecated The workflows of the user's former registry it ran, still sent for older
   * desktops; `automationIds` replaces it.
   */
  workflowIds: z.array(recordId).max(50).optional(),
  /**
   * The user's automations (ids of the task provider's `list`) it may run for a task; empty: all
   * of them. Absent from older servers.
   */
  automationIds: z.array(recordId).max(50).optional(),
  /** The automation a task naming none runs with; null: an agent does it alone. */
  defaultAutomation: recordId.nullable().optional(),
  /**
   * The viewer's own conversation of the project, which its agent leads: each member has theirs
   * (it runs as them); null until they first open the project.
   */
  conversationId: z.uuid().nullable(),
  archivedAt: z.string().nullable(),
  /** Whether the user may change or archive it; absent: yes. */
  canEdit: z.boolean().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/** An item of a run's plan: done, under way, waiting on the user, next, or in a later wave. */
export const assistantRunPlanItemSchema = z.object({
  title: z.string().trim().min(1).max(200),
  state: z.enum(['done', 'running', 'waiting', 'failed', 'next', 'later']),
  /** The conversation that does it, once it started. */
  conversationId: z.uuid().optional(),
  /** Its wave, from 1. */
  wave: z.number().int().min(1).optional(),
});

export const assistantRunPlanSchema = z.object({
  goal: z.string().max(200),
  wave: z.object({ current: z.number().int().min(1), total: z.number().int().min(1) }).nullable(),
  items: z.array(assistantRunPlanItemSchema).max(40),
});

/**
 * A task: one run of a workflow, or work an agent does alone (`workflowId` and `definition`
 * null, docs/adr-tasks-and-automations.md), as a child conversation of the one that asked for it.
 */
export const conversationWorkflowRunSchema = z.object({
  /** The automation it runs (a workflow of the former registry, for a task from before). */
  workflowId: recordId.nullable(),
  /** That automation's name when the task started; absent for a task from before. */
  automationName: z.string().max(160).nullable().optional(),
  /** The provider's definition it runs. */
  definition: z.string().min(1).max(512).nullable(),
  runId: z.string().min(1).max(256),
  /** The step running now or waiting, by its display name ("Review"). */
  step: z.string().max(200).nullable(),
  /** The review round inside a loop ("round 2 of 3"), when there is one. */
  round: z.object({ current: z.number().int().min(1), max: z.number().int().min(1) }).nullable(),
  state: z.enum(['running', 'waiting', 'succeeded', 'failed', 'stopped']),
  /** Cost of the run so far, in US dollars. */
  costUsd: z.number().nonnegative().nullable(),
  /**
   * A run that plans its work (Work as a team): its goal, its current wave and its items, one
   * state each, planned ones included, as the plan card shows them while the run goes (§2.4).
   */
  plan: assistantRunPlanSchema.nullable().optional(),
  /** What the task was asked and by whom: a queued task starts with it once it has a place. */
  request: z
    .object({ text: z.string().max(100_000), requester: z.string().max(200) })
    .nullable()
    .optional(),
});

/**
 * Where a conversation sits: its workspace, its project, its parent (child conversations).
 * `workspaceId` may be left out (docs/adr-assistants.md: workspaces go): read as null.
 */
export const conversationPlacementSchema = z.object({
  workspaceId: recordId.nullable().default(null),
  projectId: recordId.nullable(),
  parentConversationId: z.uuid().nullable(),
  workflowRun: conversationWorkflowRunSchema.nullable(),
});

// Requests

export const createWorkspaceInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  organizationId: z.string().nullable().default(null),
  instructions: z.string().max(20_000).optional(),
  repositoryIds: z.array(recordId).max(200).optional(),
});

export const updateWorkspaceInputSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    instructions: z.string().max(20_000),
    repositoryIds: z.array(recordId).max(200),
    workflows: z.array(workspaceWorkflowSchema).max(50),
  })
  .partial();

export const createAssistantInputSchema = z.object({
  /** @deprecated Sent by older desktops, not read: an assistant sits in no workspace. */
  workspaceId: recordId.optional(),
  name: z.string().trim().min(1).max(80),
  goal: z.string().max(2_000).default(''),
  color: assistantColorSchema,
  repositoryIds: z.array(recordId).max(50).default([]),
  memberAgentIds: z.array(recordId).max(16).default([]),
  /** The avatar it speaks as; taken from the user's assistant that had it. */
  avatarId: recordId.nullable().optional(),
  /** @deprecated Sent by older desktops, not read: `automationIds` replaces it. */
  workflowIds: z.array(recordId).max(50).optional(),
  /** The automations it may run; empty: all. */
  automationIds: z.array(recordId).max(50).optional(),
  /** Its default automation; left out: the provider's default; null: an agent alone. */
  defaultAutomation: recordId.nullable().optional(),
});

export const updateAssistantInputSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    goal: z.string().max(2_000),
    color: assistantColorSchema,
    repositoryIds: z.array(recordId).max(50),
    memberAgentIds: z.array(recordId).max(16),
    /** @deprecated Sent by older desktops, not read: schedules are automations. */
    wakes: z.array(assistantWakeSchema).max(20),
    instructions: z.string().max(20_000),
    /** The avatar it speaks as, taken from the user's assistant that had it; null: none. */
    avatarId: recordId.nullable(),
    /** @deprecated Sent by older desktops, not read: `automationIds` replaces it. */
    workflowIds: z.array(recordId).max(50),
    automationIds: z.array(recordId).max(50),
    /** Null: its tasks are done by an agent alone, unless they name an automation. */
    defaultAutomation: recordId.nullable(),
    /** Set to archive (archives finished child conversations too), null to restore. */
    archived: z.boolean(),
  })
  .partial();

/**
 * "Move to…": into an assistant (`projectId`), or out of any (`null`).
 */
export const moveConversationInputSchema = z.object({
  /** @deprecated Sent by older desktops, not read (docs/adr-assistants.md). */
  workspaceId: recordId.optional(),
  projectId: recordId.nullable(),
  /**
   * `null` detaches a child conversation from its parent ("Detach", D49). Left out, a child
   * stays under its parent when the parent is in the destination project.
   */
  parentConversationId: z.null().optional(),
});

/**
 * One of the user's automations a task may run (`tasks.v1`'s `list`,
 * docs/adr-tasks-and-automations.md): `default` marks the one the provider installed as a new
 * user's default.
 */
export const taskAutomationSchema = z.object({
  id: recordId,
  name: z.string().min(1).max(160),
  summary: z.string().max(500).default(''),
  default: z.boolean().optional(),
  /** The plugin that runs it, whose app edits it. */
  provider: z.string().min(1).max(128).optional(),
});

/** A task of a project, as the Tasks tab lists it (D65). */
export const assistantTaskSchema = z.object({
  conversationId: z.uuid(),
  /** The conversation that asked for it, which it reports to. */
  parentConversationId: z.uuid().nullable(),
  /** The viewer's own: another member's task shows, but opens only for them. */
  mine: z.boolean(),
  title: z.string(),
  workflowRun: conversationWorkflowRunSchema,
  branch: z.string().nullable(),
  pullRequest: z
    .object({ number: z.number().int(), state: z.enum(['open', 'merged', 'closed']) })
    .nullable(),
  /** Agent steps of the task (a review by another agent), nested under it. */
  children: z.array(
    z.object({
      conversationId: z.uuid(),
      title: z.string(),
      agentName: z.string(),
      costUsd: z.number().nonnegative().nullable(),
    }),
  ),
});

export type CrowdexWorkspace = z.infer<typeof crowdexWorkspaceSchema>;
export type WorkspaceWorkflow = z.infer<typeof workspaceWorkflowSchema>;
export type CrowdexAssistant = z.infer<typeof crowdexAssistantSchema>;
export type AssistantWake = z.infer<typeof assistantWakeSchema>;
export type ConversationWorkflowRun = z.infer<typeof conversationWorkflowRunSchema>;
export type AssistantRunPlan = z.infer<typeof assistantRunPlanSchema>;
export type AssistantRunPlanItem = z.infer<typeof assistantRunPlanItemSchema>;
export type ConversationPlacement = z.infer<typeof conversationPlacementSchema>;
export type CreateWorkspaceInput = z.input<typeof createWorkspaceInputSchema>;
export type UpdateWorkspaceInput = z.infer<typeof updateWorkspaceInputSchema>;
/** What a client sends to create an assistant: no workspace (docs/adr-assistants.md). */
export type CreateAssistantInput = z.input<typeof createAssistantInputSchema>;
export type UpdateAssistantInput = z.infer<typeof updateAssistantInputSchema>;
/** What a client sends to move a conversation: into an assistant, or out of any. */
export type MoveConversationInput = z.input<typeof moveConversationInputSchema>;
export type AssistantTask = z.infer<typeof assistantTaskSchema>;
export type TaskAutomation = z.infer<typeof taskAutomationSchema>;

/**
 * What `GET /api/assistants` answers (docs/adr-assistants.md): the user's assistants, home
 * first, with no workspace grouping; the placement of every conversation that has one.
 * `GET /api/workspaces` answers its older shape, `assistants` and `workflows` added.
 */
export const assistantDirectorySchema = z.object({
  assistants: z.array(crowdexAssistantSchema),
  /** @deprecated The former registry of workflows: empty (docs/adr-tasks-and-automations.md). */
  workflows: z.array(workspaceWorkflowSchema),
  /** @deprecated Empty, as `workflows`. */
  workflowAssistants: z.record(z.string(), z.number().int().nonnegative()),
  placements: z.record(z.string(), conversationPlacementSchema),
});

export type AssistantDirectory = z.infer<typeof assistantDirectorySchema>;

/*
 * Former names (docs/adr-assistants.md: a project is an assistant), kept for code outside this
 * repository that imports them until the drop and rename after one desktop release.
 */
/** @deprecated Use `assistantColorSchema`. */
export const projectColorSchema = assistantColorSchema;
/** @deprecated Use `ASSISTANT_COLORS`. */
export const PROJECT_COLORS = ASSISTANT_COLORS;
/** @deprecated Use `assistantWakeSchema`. */
export const projectWakeSchema = assistantWakeSchema;
/** @deprecated Use `crowdexAssistantSchema`. */
export const crowdexProjectSchema = crowdexAssistantSchema;
/** @deprecated Use `assistantRunPlanItemSchema`. */
export const projectRunPlanItemSchema = assistantRunPlanItemSchema;
/** @deprecated Use `assistantRunPlanSchema`. */
export const projectRunPlanSchema = assistantRunPlanSchema;
/** @deprecated Use `createAssistantInputSchema`. */
export const createProjectInputSchema = createAssistantInputSchema;
/** @deprecated Use `updateAssistantInputSchema`. */
export const updateProjectInputSchema = updateAssistantInputSchema;
/** @deprecated Use `assistantTaskSchema`. */
export const projectTaskSchema = assistantTaskSchema;
/** @deprecated Use `CrowdexAssistant`. */
export type CrowdexProject = CrowdexAssistant;
/** @deprecated Use `AssistantWake`. */
export type ProjectWake = AssistantWake;
/** @deprecated Use `AssistantRunPlan`. */
export type ProjectRunPlan = AssistantRunPlan;
/** @deprecated Use `AssistantRunPlanItem`. */
export type ProjectRunPlanItem = AssistantRunPlanItem;
/** @deprecated Use `CreateAssistantInput`. */
export type CreateProjectInput = CreateAssistantInput;
/** @deprecated Use `UpdateAssistantInput`. */
export type UpdateProjectInput = UpdateAssistantInput;
/** @deprecated Use `AssistantTask`. */
export type ProjectTask = AssistantTask;
